<template>
  <div class="content">
    <h1 class="has-text-centered">Betriebssysteme</h1>
    <div class="box">
      <p>Eine kompakte Zusammenfassung mit Infos über Betriebssysteme.</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-operating-systems',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: 'Betriebsysteme | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'In fast jedem modernen Gerät befindet sich ein Betriebssystem. Es bildet die Schnittstelle zwischen Anwendersoftware und Hardware.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was ist ein Betriebssystem?',
          answer:
            '<p>' +
            '   Ein Betriebssystem ...' +
            '</p>' +
            '<ul>' +
            '  <li>... bildet die Schnittstelle zwischen Anwendersoftware und Hardware.</li>' +
            '  <li>... ist eine Zusammenstellung verschiedener Programme zur Verwaltung der Systemressourcen.</li>' +
            '  <li>... befindet sich mittlerweile in fast jedem modernen Gerät.</li>' +
            '  <ul>' +
            '    <li>Computer, Laptop</li>' +
            '    <li>Smartphones, Tablet</li>' +
            '    <li>Spielekonsolen</li>' +
            '    <li>Smart TVs</li>' +
            '    <li>Autos</li>' +
            '    <li>und viele andere</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <p>' +
            '    Die verbreitesten Betriebsysteme sind Microsoft Windows, Linux und MacOS. Zusätzliche gibt es auch viele eigene Betriebssysteme für Microcontroller und spezielle Anwendungen.<br>' +
            '    Auf <a href="https://de.wikipedia.org/wiki/Liste_von_Betriebssystemen" target="_blank">dieser Wikipedia-Seite</a> findest du eine umfrangreiche Auflistung von verschiedenen Betriebssystemen.' +
            '  </p>'
        },
        {
          question: 'Wie ist ein Betriebssystem aufgebaut?',
          answer:
            '<ul>' +
            '  <li>Ein Betriebssystem steht zwischen Hardware und Software und ist in mehreren Schichten aufgebaut.</li>' +
            '  <li>Auf unterster Schicht befindet sich der Kernel welcher die Kommunikation und Verwaltung der Hardware übernimmt.</li>' +
            '  <li>Auf dem Kernel bauen weitere Schichten auf, die sich immer weiter von der Interaktion mit Hardware entfernen. Die Benutzeroberfläche befindet sich in der obersten Schicht.</li>' +
            '  <li>Eine Schicht kommuniziert nur mit der Schicht über oder unter ihr.</li>' +
            '  <li>Alle Bestandteile des Betriebssystem die nicht zum Kernel gehören nennt man Systemprogramme.</li>' +
            '  <li>Typische Systemprogramme sind beispielsweise:</li>' +
            '  <ul>' +
            '    <li>Befehle zur Manipulation von Dateien und Verzeichnissen (Umbenennen, Löschen oder Kopieren)</li>' +
            '    <li>Unterschiedliche Steuerungs- und Analysewerkzeuge</li>' +
            '  </ul>' +
            '  <li>Systemprogramme können Funktionen des Kernels über sogenannte "Systemaufrufe" nutzen</li>' +
            '  <li>Anwenderprogramme sind Programme die nicht mehr zum Betriebssystem gehören</li>' +
            '  <li>Auch Anwenderprogramme können Funktionen des Kernels über "Systemaufrufe" nutzen</li>' +
            '  <li>Beispiele für Systemaufrufe</li>' +
            '  <ul>' +
            '    <li>Lesen, Schreiben, Löschen und Erstellen von Dateien</li>' +
            '    <li>Erzeugen und Stoppen von Prozessen</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was sind die Aufgaben eines Betriebssystems?',
          answer:
            '<ul>' + '  <li>Speicherverwaltung</li>' + '  <li>Prozessverwaltung</li>' + '  <li>Geräte- und Dateiverwaltung</li>' + '  <li>Rechteverwaltung</li>' + '  <li>Abstraktion</li>' + '</ul>'
        },
        {
          question: 'Was sind Systemressourcen?',
          answer: '<ul>' + '  <li>Arbeitsspeicher</li>' + '  <li>Festplatten</li>' + '  <li>Ein- und Ausgabe-Geräte (Tastatur, Maus, Netzwerkverkehr ...)</li>' + '  <li>Prozessorzeit</li>' + '</ul>'
        },
        {
          question: 'Was sind Aufgaben der Speicherverwaltung?',
          answer: '<ul>' + '  <li>Zuweisung von Hauptspeicher an Benutzeraufträge bzw. Tasks</li>' + '  <li>Protokollierung der Speichernutzung</li>' + '</ul>'
        },
        {
          question: 'Was sind Aufgaben der Prozessverwaltung?',
          answer:
            '<ul>' +
            '  <li>Überwachung von Speicherzugriffen</li>' +
            '  <li>Beenden von Prozessen bei Zugriffsverletzungen</li>' +
            '  <li>Erzeugung neuer Prozesse</li>' +
            '  <li>Kommunikation und Synchronisation aller Prozesse</li>' +
            '</ul>'
        },
        {
          question: 'Was sind Aufgaben der Geräte- und Dateiverwaltung?',
          answer:
            '<ul>' +
            '  <li>Verwaltung von Ein- und Ausgabegeräten sowie deren Vermittlungseinheiten (Datenkanäle, Steuereinheiten)</li>' +
            '  <li>Vermeidung von Gerätekonflikten</li>' +
            '  <li>Initiierung, Überwachung und Terminierung von Ein- und Ausgabevorgängen</li>' +
            '  <li>Verwaltung des Dateisystems</li>' +
            '</ul>'
        },
        {
          question: 'Was ist die Benutzeroberfläche?',
          answer:
            '<ul>' +
            '  <li>Ermöglichen dem Benutzer die Kommunikation mit dem Betriebssystem</li>' +
            '  <li>Es gibt zwei grundlegende Arten von Benutzeroberflächen</li>' +
            '  <li>Kommandozeile (auch "Konsole" genannt)</li>' +
            '  <li>Grafische Oberfläche (kurz GUI - engl. "Graphical User Interface")</li>' +
            '</ul>'
        },
        {
          question: 'Was ist ein Kernel?',
          answer:
            '<ul>' +
            '  <li>Software auf unterster Schicht des Betriebssystems</li>' +
            '  <li>Läuft permanent im Hintergrund</li>' +
            '  <li>Zentraler Bestandteil eines Betriebssystems (der Kern eines Betriebssystems)</li>' +
            '  <li>Bestimmt Prozess und Datenorganisation</li>' +
            '  <li>Alle weiteren Softwarebestandteile bauen auf dem Kernel auf</li>' +
            '  <li>Hat direkten Zugriff auf die Hardware</li>' +
            '</ul>'
        },
        {
          question: 'Was sind die Bestandteile eines Kernels?',
          answer:
            '<ul>' +
            '  <li>Schnittstelle zur Hardware</li>' +
            '  <ul>' +
            '    <li>In Form von Kernel-Modulen können Gerätetreiber programmiert werden</li>' +
            '  </ul>' +
            '  <li>Speicherverwaltung inklusive virtuellem Hauptspeicher</li>' +
            '  <li>Prozessverwaltung ("Scheduler")</li>' +
            '  <li>Geräteverwaltung (Gerätetreiber in Form von Kernel-Modulen)</li>' +
            '  <li>Dateisystem</li>' +
            '  <li>Die drei wichtigsten Typen von Kernel</li>' +
            '  <ul>' +
            '    <li>' +
            '      <strong>Monotlithischer Kernel</strong>' +
            '      <br />' +
            '      Dieser Kernel deckt alle oben genannten Bestandteile ab' +
            '    </li>' +
            '    <li>' +
            '      <strong>Mikro-Kernel</strong>' +
            '      <br />' +
            '      Dieser Kernel deckt nur die wichtigsten der oben genannten Bestandteile ab (u.a. Prozessverwaltung, Speicherverwaltung)' +
            '    </li>' +
            '    <li>' +
            '      <strong>Hybrid-Kernel</strong>' +
            '      <br />' +
            '      Ein Kompromiss zwischen Monolithischen und Mikro-Kernel' +
            '    </li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgabe hat der Scheduler?',
          answer:
            '<ul>' +
            '  <li>Regelt die zeitliche Ausführung mehrerer Prozesse in Betriebssystemen</li>' +
            '  <li>Arten von Schedulern</li>' +
            '  <ul>' +
            '    <li>' +
            '      <strong>Nicht unterbrechende Scheduler ("non preemptive", "kooperativ")</strong>' +
            '      <br />' +
            '      Weisen die Ressourcen der CPU einem Prozess solange zu, bis der Prozess die CPU selbst wieder freigibt' +
            '    </li>' +
            '    <li>' +
            '      <strong>Unterbrechende Scheduler ("präemptiv")</strong>' +
            '      <br />' +
            '      Weisen die Resourcen der CPU einem Prozess nur eine bestimmte Zeit lang zu' +
            '    </li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist ein Prozess?',
          answer: '<p>Ein Prozess ist ein in Ausführung befindliches Programm</p>'
        },
        {
          question: 'Was sind die Aufgaben eines Kernels?',
          answer:
            '<ul>' +
            '  <li>Kümmert sich um die Kommunikation mit Hardware und Programmen untereinander</li>' +
            '  <li>Verwaltet die Zuteilung von Hardwareressourcen</li>' +
            '  <li>Zuständig für die Verwaltung von Daten (auch wie und wdie Daten abgelegt werden)</li>' +
            '  <li>Ermöglicht Multitasking und Multiuserbetrieb</li>' +
            '</ul>'
        }
      ]
    }
  }
}
</script>
